import React, { useEffect } from 'react';
import { CodeTypeEnum, EntityTypeEnum } from '@api/ringil-api.v2.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import ResponsiblePersonTag from '@components/framework/tags/ResponsiblePersonTag.tsx';
import NotifyUsersTag from '@components/RingilComponents/tags/NotifyUsersTag.tsx';
import InternalInfoNotifyByRoleTag from '@components/framework/tags/InternalInfoNotifyByRoleTag.tsx';
import InternalInfoInvoiceState from '@components/framework/tags/InternalInfoInvoiceStateTag.tsx';
import InternalInfoDisponentTag from '@components/framework/tags/InternalInfoDisponentTag.tsx';
import InternalInfoPairingSymbol from '@components/framework/tags/InternalInfoPairingSymbol.tsx';
import InternalNoteTag from '@components/framework/tags/InternalNoteTag.tsx';
import InternalInfoRebillingTag from '@components/framework/tags/InternalInfoRebillingTag.tsx';
import InternalInfoFoldersTag from '@components/framework/tags/InternalInfoFoldersTag.tsx';
import useFolders from '@hooks/use-folders/useFolders.tsx';
import { findValidationMessage } from '@utils/validation';
import InternalInfoCustomDeclarantTag from '@components/framework/tags/InternalInfoCustomDeclarantTag.tsx';
import InternalInfoCatalogTag from '@components/framework/tags/InternalInfoCatalogTag';
import { RoleEnum } from '@type/enums.ts';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Form } from '@hooks/useForm/useForm.tsx';
import { NewExpeditionForm } from '@app/pages/expedition/create/types.ts';

interface ComponentProps {
  form: Form<NewExpeditionForm>;
  /** RD-2977: the company requires at least one folder on every shipment */
  folderRequired?: boolean;
}

/** Bookkeeping the customer keeps for itself — never shown to carriers. */
const InternalInfoSection: React.FC<ComponentProps> = ({ form, folderRequired = false }) => {
  const { tr } = useTranslationLgs();
  // RD-2977: the LOV only holds folders the user may access; a user with exactly one gets it pre-filled
  const accessibleFolders = useFolders(EntityTypeEnum.EXPEDITION);
  useEffect(() => {
    if (!folderRequired) return;
    if (accessibleFolders.length === 1 && (form.values.folders?.length ?? 0) === 0) {
      const only = accessibleFolders[0];
      form.useProduce(d => {
        d.folders = [only];
      });
    }
  }, [folderRequired, accessibleFolders]);

  return (
    <>
      <COHeadline variant={'h3'} title={tr('CreateExpedition.interniInfo', 'Internal info')} />
      <div className={'flex flex-col gap-4'}>
        <Ringil3BadgeRow className={'flex flex-row gap-4'}>
          <ResponsiblePersonTag
            person={form.values.person}
            onChange={v => {
              form.useProduce(d => {
                d.person = v;
              });
            }}
          />

          <NotifyUsersTag
            notifiedTargets={form.values.notifiedTargets}
            onChange={notifyTargets => form.setFieldValue(form.names.notifiedTargets, notifyTargets)}
          />

          <InternalInfoNotifyByRoleTag
            notifyRoleName={RoleEnum.ROLE_EXP_NOTIFY}
            isActive={true}
            onChange={v => {
              form.useProduce(d => {
                d.notifyByRole = v;
              });
            }}
          />
        </Ringil3BadgeRow>
        <Ringil3BadgeRow>
          <InternalInfoInvoiceState
            value={form.values.invoiceState}
            onChange={v => {
              form.useProduce(d => {
                d.invoiceState = v;
              });
            }}
          />

          <InternalInfoDisponentTag
            disponentPerson={form.values.disponentPerson}
            onChange={v => {
              form.useProduce(d => {
                d.disponentPerson = v;
              });
            }}
          />

          <InternalInfoPairingSymbol
            value={form.values.pairingSymbol}
            onChange={v => {
              form.useProduce(d => {
                d.pairingSymbol = v;
              });
            }}
          />

          <InternalNoteTag
            value={form.values.internalNote}
            onChange={v => {
              form.useProduce(d => {
                d.internalNote = v;
              });
            }}
          />

          <InternalInfoRebillingTag
            value={form.values.rebilling}
            onChange={v => {
              form.useProduce(d => {
                d.rebilling = v;
              });
            }}
          />

          <InternalInfoFoldersTag
            folders={form.values.folders ?? []}
            required={folderRequired}
            errorMessage={findValidationMessage(form.validationErrors, 'folders', tr)}
            onChange={v => {
              form.useProduce(d => {
                d.folders = v;
              });
            }}
            entityType={EntityTypeEnum.EXPEDITION}
          />
          <InternalInfoCustomDeclarantTag
            customDeclarationCompanies={form.values.customsDeclarationCompanies || []}
            onChange={v => {
              form.useProduce(d => {
                d.customsDeclarationCompanies = v;
              });
            }}
          />
          <InternalInfoCatalogTag
            type={CodeTypeEnum.ACCOUNT}
            value={form.values.internalInfoAccountCodeListId}
            onChange={v => {
              form.useProduce(d => {
                d.internalInfoAccountCodeListId = v;
              });
            }}
          />
          <InternalInfoCatalogTag
            type={CodeTypeEnum.COST_CENTER}
            value={form.values.internalInfoCostCenterCodeListId}
            onChange={v => {
              form.useProduce(d => {
                d.internalInfoCostCenterCodeListId = v;
              });
            }}
          />
          <InternalInfoCatalogTag
            type={CodeTypeEnum.COST_UNIT}
            value={form.values.internalInfoCostUnitCodeListId}
            onChange={v => {
              form.useProduce(d => {
                d.internalInfoCostUnitCodeListId = v;
              });
            }}
          />
          <InternalInfoCatalogTag
            type={CodeTypeEnum.PROJECT}
            value={form.values.internalInfoProjectCodeListId}
            onChange={v => {
              form.useProduce(d => {
                d.internalInfoProjectCodeListId = v;
              });
            }}
          />
        </Ringil3BadgeRow>
      </div>
    </>
  );
};

export default InternalInfoSection;